import { AttendeeUserDefault } from '@/types/all-attendees.types';
import TableData from '@layouts/layout-table/layout-table-data';
import BrandliveAvatar from '@ui/brandlive-avatar/brandlive-avatar';
import { formatDate, withDateOnly } from '@/utils/date/format-date';
import type { ColumnDefinition } from './profile-column-definitions';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { formatWatchtime } from '../../utils';

type ProfileListCellProps = {
  column: ColumnDefinition;
  user: AttendeeUserDefault;
};

const ProfileListCell = ({ column, user }: ProfileListCellProps) => {
  const { id, columnWidth, registrationQuestion } = column;
  const style = { width: columnWidth, minWidth: columnWidth };
  const u = user as Record<string, unknown>;

  if (registrationQuestion != null) {
    return (
      <TableData style={{ width: '150px', minWidth: '150px' }}>
        {String(u[String(registrationQuestion)] ?? '—')}
      </TableData>
    );
  }

  switch (id) {
    case 'full_name':
      return (
        <TableData style={style}>
          <div className="flex ai-center gap-8">
            <BrandliveAvatar profile={{ picture: user.avatar, email: user.email }} />
            <span className="S2">{user.full_name ?? '—'}</span>
          </div>
        </TableData>
      );
    case 'email':
      return <TableData style={style}>{user.email}</TableData>;
    case 'job_title':
      return <TableData style={style}>{user.job_title ?? '—'}</TableData>;
    case 'company_name':
      return <TableData style={style}>{user.company_name ?? '—'}</TableData>;
    case 'industry':
      return <TableData style={style}>{user.industry ?? '—'}</TableData>;
    case 'phone_number':
      return <TableData style={style}>{user.phone_number ?? '—'}</TableData>;
    case 'department':
      return <TableData style={style}>{user.department ?? '—'}</TableData>;
    case 'city':
      return <TableData style={style}>{user.city || '—'}</TableData>;
    case 'state':
      return <TableData style={style}>{user.state || '—'}</TableData>;
    case 'countryCode':
      return <TableData style={style}>{user.countryCode || '—'}</TableData>;
    case 'timezone':
      return <TableData style={style}>{user.timezone ?? '—'}</TableData>;
    case 'language':
      return <TableData style={style}>{user.preferred_language ?? '—'}</TableData>;
    case 'linkedIn':
      return <TableData style={style}>{String(u['linkedIn'] ?? '—')}</TableData>;
    case 'twitter':
      return <TableData style={style}>{String(u['twitter'] ?? '—')}</TableData>;
    case 'total_registered':
      return <TableData style={style}>{user.total_registered ?? '—'}</TableData>;
    case 'total_viewed':
      return <TableData style={style}>{user.total_viewed ?? '—'}</TableData>;
    case 'no_shows':
      return <TableData style={style}>{user.no_shows ?? '—'}</TableData>;
    case 'last_watch':
      return <TableData style={style}>{user.last_watch ? formatDate(user.last_watch, withDateOnly) : '—'}</TableData>;
    case 'first_watch':
      return <TableData style={style}>{user.first_watch ? formatDate(user.first_watch, withDateOnly) : '—'}</TableData>;
    case 'total_seconds':
      return <TableData style={style}>{formatWatchtime(user.total_seconds)}</TableData>;
    case 'event_seconds':
      return <TableData style={style}>{formatWatchtime(u.total_seconds as number | null | undefined)}</TableData>;
    case 'source':
      return <TableData style={style}>{String(u.source ?? '—')}</TableData>;
    case 'attended': {
      const attended = u.attended === true || u.attended === 'true';
      return (
        <TableData style={style}>
          <Icon name={attended ? ICONS.CHECKMARK : ICONS.CLOSE} size={12} color="var(--global-ico-text-1)" />
        </TableData>
      );
    }
    case 'attended_phase':
      return <TableData style={style}>{String(u.attended_phase ?? '—')}</TableData>;
    case 'created_date':
      return (
        <TableData style={style}>{u.created_date ? formatDate(String(u.created_date), withDateOnly) : '—'}</TableData>
      );
    case 'referrer':
      return <TableData style={style}>{String(u.referrer ?? '—')}</TableData>;
    case 'utm_source':
    case 'utm_medium':
    case 'utm_campaign':
    case 'utm_content': {
      const utmParams = u.utm_params as Record<string, string> | null | undefined;
      return <TableData style={style}>{utmParams?.[id] ?? '—'}</TableData>;
    }
    case 'num_questions':
      return <TableData style={style}>{String(u.num_questions ?? '—')}</TableData>;
    case 'num_comments':
      return <TableData style={style}>{String(u.num_comments ?? '—')}</TableData>;
    case 'num_reactions':
      return <TableData style={style}>{String(u.num_reactions ?? '—')}</TableData>;
    case 'device_type':
      return <TableData style={style}>{String(u.device_type ?? '—')}</TableData>;
    case 'browser':
      return <TableData style={style}>{String(u.browser ?? '—')}</TableData>;
    case 'engagement_score':
      return <TableData style={style}>{String(u.engagement_score ?? '—')}</TableData>;
    case 'associated_campaigns':
      return <TableData style={style}>{String(u.associated_campaigns ?? '—')}</TableData>;
    default:
      return null;
  }
};

export default ProfileListCell;
